import { PlusCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ReactNode, useEffect, useRef, useState } from "react";
import { cx } from "../../../utils/classnames";

interface FilterPopoverProps {
  label: string;
  // What is applied, already summarised; empty means the filter is off.
  value?: string;
  onClear: () => void;
  disabled?: boolean;
  disabledTitle?: string;
  panelClassName?: string;
  children: (close: () => void) => ReactNode;
}

// One filter of the bar: a dashed "+ Label" button while off, a solid "Label: value ×" chip
// once applied, so the chips row is itself the list of what is filtered. Either opens the
// filter's panel; the × clears it without opening anything.
const FilterPopover = ({
  label,
  value,
  onClear,
  disabled,
  disabledTitle,
  panelClassName = "w-72",
  children
}: FilterPopoverProps) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = !!value;

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <div
        className={cx([
          "flex items-center rounded-full border text-sm duration-200",
          active
            ? "border-primary/40 bg-primary/5 text-black"
            : "border-dashed border-gray-300 text-neutral",
          disabled ? "opacity-50" : "hover:border-primary/60"
        ])}
      >
        <button
          type="button"
          disabled={disabled}
          title={disabled ? disabledTitle : undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cx([
            "flex items-center gap-1.5 py-1.5 disabled:cursor-not-allowed",
            active ? "pl-3 pr-1" : "px-3"
          ])}
        >
          {!active && <PlusCircleIcon className="w-4 h-4" strokeWidth={2} />}
          <span className={active ? "text-neutral" : undefined}>{label}</span>
          {active && (
            <>
              <span className="text-gray-300">|</span>
              <span className="max-w-[220px] truncate font-medium text-primary">{value}</span>
            </>
          )}
        </button>
        {active && (
          <button
            type="button"
            onClick={onClear}
            aria-label={`Hiq filtrin ${label}`}
            className="mr-1 rounded-full p-1 text-neutral hover:bg-primary/10 hover:text-black"
          >
            <XMarkIcon className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        )}
      </div>
      {open && (
        <div
          role="dialog"
          aria-label={label}
          className={cx([
            "absolute left-0 top-[calc(100%+0.5rem)] z-30 rounded-lg border border-gray-200",
            "bg-white p-3 shadow-lg",
            panelClassName
          ])}
        >
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral">{label}</p>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
};

export default FilterPopover;
